const BASE_PATH = '/api'

// 소문자만 입력한다.
// /api/분류별 base path/요청대상
export const API_URL = {
  CHECK_HAS_TABLES: BASE_PATH + '/check-has-tables',
  PING: BASE_PATH + '/ping',
  INIT_DB_TABLES: BASE_PATH + '/init-db-tables',
  AUTH: {
    LOGIN: BASE_PATH + '/login',
    REFRESH: BASE_PATH + '/refreshToken',
    CHECK_ID: BASE_PATH + '/check-id',
    REQUEST_ACCOUNT: BASE_PATH + '/request-account',
  },
  TEST: {
    PING: BASE_PATH + '/ping',
  },
  PROFILE: {
    INFO: BASE_PATH + '/profile',
    MODIFY: BASE_PATH + '/profile',
  },

  MENU: {
    LIST: BASE_PATH + '/menu',
  },

  USER: {
    LIST: BASE_PATH + '/users',
    DIRECTORY: BASE_PATH + '/user-directory',
    APPROVE: (userNo: number) => BASE_PATH + `/users/${userNo}/approve`,
    REJECT: (userNo: number) => BASE_PATH + `/users/${userNo}/reject`,
    LOCK: (userNo: number) => BASE_PATH + `/users/${userNo}/lock`,
    MODIFY: (userNo: number) => BASE_PATH + `/users/${userNo}`,
    DELETE: (userNo: number) => BASE_PATH + `/users/${userNo}`,
  },

  OAUTH: {
    AUTHORIZE: BASE_PATH + '/oauth/authorize',
    AUTH_CODE: BASE_PATH + '/oauth/code',
    TOKEN: BASE_PATH + '/oauth/token',
    CLIENT: BASE_PATH + '/oauth/client',
  },

  WORKSPACE: {
    RECENT_SESSIONS: BASE_PATH + '/workspace/recent-sessions',
    MY_PROJECTS: BASE_PATH + '/workspace/my-projects',
    PARTICIPATED_PROJECTS: BASE_PATH + '/workspace/participated-projects',
    PINNED_DATA: BASE_PATH + '/workspace/pinned-data',
  },

  PROJECT: {
    LIST: BASE_PATH + '/projects',
    // Explorer 좌측 트리용: 전체 프로젝트 + 세션 요약(이름, 목표, 태그, 데이터 수)을 한 번에 받는다
    TREE: BASE_PATH + '/projects/tree',
    DETAIL: (projectId: number | string) => BASE_PATH + `/projects/${projectId}`,
    CREATE: BASE_PATH + '/projects',
    CREATE_SESSION: (projectId: number | string) => BASE_PATH + `/projects/${projectId}/sessions`,
    UPDATE: (projectId: number | string) => BASE_PATH + `/projects/${projectId}`,
    UPDATE_MEMBERS: (projectId: number | string) => BASE_PATH + `/projects/${projectId}/members`,
    DELETE: (projectId: number | string) => BASE_PATH + `/projects/${projectId}`,
    CLOSE: (projectId: number | string) => BASE_PATH + `/projects/${projectId}/close`,
    REOPEN: (projectId: number | string) => BASE_PATH + `/projects/${projectId}/reopen`,
  },

  LAB_NOTE: {
    LIST: (projectId: number | string) => BASE_PATH + `/projects/${projectId}/lab-notes`,
    CREATE: (projectId: number | string) => BASE_PATH + `/projects/${projectId}/lab-notes`,
    UPDATE: (projectId: number | string, noteId: number | string) => BASE_PATH + `/projects/${projectId}/lab-notes/${noteId}`,
    DELETE: (projectId: number | string, noteId: number | string) => BASE_PATH + `/projects/${projectId}/lab-notes/${noteId}`,
    CREATE_REPLY: (projectId: number | string, noteId: number | string) => BASE_PATH + `/projects/${projectId}/lab-notes/${noteId}/replies`,
    UPDATE_REPLY: (projectId: number | string, noteId: number | string, replyId: number | string) => BASE_PATH + `/projects/${projectId}/lab-notes/${noteId}/replies/${replyId}`,
    DELETE_REPLY: (projectId: number | string, noteId: number | string, replyId: number | string) => BASE_PATH + `/projects/${projectId}/lab-notes/${noteId}/replies/${replyId}`,
  },

  ASSIGNMENT: {
    LIST: BASE_PATH + '/assignments',
    CREATE: BASE_PATH + '/assignments',
    MODIFY: (assignmentId: number | string) => BASE_PATH + `/assignments/${assignmentId}`,
    DELETE: (assignmentId: number | string) => BASE_PATH + `/assignments/${assignmentId}`,
  },

  TAG: {
    LIST: BASE_PATH + '/tags',
  },

  MATERIAL: {
    LIST: BASE_PATH + '/materials',
    CREATE: BASE_PATH + '/materials',
    MODIFY: (materialId: number | string) => BASE_PATH + `/materials/${materialId}`,
    DELETE: (materialId: number | string) => BASE_PATH + `/materials/${materialId}`,
    MERGE: (materialId: number | string) => BASE_PATH + `/materials/${materialId}/merge`,
  },

  MODALITY: {
    LIST: BASE_PATH + '/modalities',
  },

  DETECTOR: {
    LIST: BASE_PATH + '/detectors',
  },

  SESSION: {
    CREATE: (projectId: number | string) => BASE_PATH + `/projects/${projectId}/sessions`,
    DETAIL: (sessionId: number | string) => BASE_PATH + `/sessions/${sessionId}`,
  },

  HOME: {
    DATA: BASE_PATH + '/home/data',
    SEARCH_DATA: BASE_PATH + '/home/search/data',
    SEARCH_ITEMS: BASE_PATH + '/home/search/items',
    SEARCH_HISTORY: BASE_PATH + '/home/search/history',
  },

  NOTIFICATION: {
    LIST: BASE_PATH + '/notifications',
    STREAM: BASE_PATH + '/notifications/stream',
    READ: (id: number | string) => BASE_PATH + `/notifications/${id}/read`,
    READ_ALL: BASE_PATH + '/notifications/read-all',
    DELETE: (id: number | string) => BASE_PATH + `/notifications/${id}`,
    DELETE_READ: BASE_PATH + '/notifications/read',
  },

  DATA: {
    DETAIL: (dataNo: number) => BASE_PATH + `/data/${dataNo}`,
    THUMBNAIL: (dataNo: number | string) => BASE_PATH + `/data/${dataNo}/thumbnail`,
    IMAGE: (dataNo: number | string, fileName?: string) =>
      fileName
        ? BASE_PATH + `/data/${dataNo}/image/${encodeURIComponent(fileName)}`
        : BASE_PATH + `/data/${dataNo}/image`,
    RAW_FILE: (dataNo: number | string, fileNo: number | string) =>
      BASE_PATH + `/data/${dataNo}/files/${fileNo}/raw`,
    FILES: (dataNo: number) => BASE_PATH + `/data/${dataNo}/files`,
    RELATED: (dataNo: number) => BASE_PATH + `/data/${dataNo}/related`,
    DOI: (dataNo: number) => BASE_PATH + `/data/${dataNo}/doi`,
    MEMO: (dataNo: number) => BASE_PATH + `/data/${dataNo}/memo`,
    RATING: (dataNo: number) => BASE_PATH + `/data/${dataNo}/rating`,
    DERIVATION: (dataNo: number) => BASE_PATH + `/data/${dataNo}/derivation`,
    DERIVATION_CANDIDATES: (dataNo: number) => BASE_PATH + `/data/${dataNo}/derivation/candidates`,
    SCRAP: (dataNo: number) => BASE_PATH + `/data/${dataNo}/scrap`,
    FAVORITE: (dataNo: number) => BASE_PATH + `/data/${dataNo}/favorite`,
    DOWNLOAD_LOG: (dataNo: number) => BASE_PATH + `/data/${dataNo}/download`,
    VIEW_LOG: (dataNo: number) => BASE_PATH + `/data/${dataNo}/view`,
    DOWNLOAD_URL: (dataNo: number, fileNo: number) =>
      BASE_PATH + `/data/${dataNo}/files/${fileNo}/download-url`,
    DOWNLOAD_START: (dataNo: number, fileNo: number) =>
      BASE_PATH + `/data/${dataNo}/files/${fileNo}/download/start`,
    DOWNLOAD_STATUS: (dataNo: number, fileNo: number, downloadNo: number) =>
      BASE_PATH + `/data/${dataNo}/files/${fileNo}/download/${downloadNo}/status`,
    DOWNLOAD_HISTORY: (dataNo: number) => BASE_PATH + `/data/${dataNo}/downloads/history`,
  },

  DOWNLOAD: {
    HISTORY: BASE_PATH + '/downloads/history',
  },

  PARTNER_REQUEST: {
    CREATE: BASE_PATH + '/partner-requests',
    MINE: BASE_PATH + '/partner-requests/mine',
    PENDING: BASE_PATH + '/partner-requests/pending',
    APPROVE: (id: number | string) => BASE_PATH + `/partner-requests/${id}/approve`,
    REJECT: (id: number | string) => BASE_PATH + `/partner-requests/${id}/reject`,
  },

  DOI: {
    REQUEST: (dataNo: number | string) => BASE_PATH + `/data/${dataNo}/doi/request`,
    RETRACT_REQUEST: (dataNo: number | string) => BASE_PATH + `/data/${dataNo}/doi/retract-request`,
    DETAIL: (dataNo: number | string) => BASE_PATH + `/data/${dataNo}/doi`,
    ADMIN_LIST: BASE_PATH + '/admin/doi/requests',
    ADMIN_APPROVE: (requestNo: number | string) => BASE_PATH + `/admin/doi/requests/${requestNo}/approve`,
    ADMIN_REJECT: (requestNo: number | string) => BASE_PATH + `/admin/doi/requests/${requestNo}/reject`,
    ADMIN_RETRACT: (requestNo: number | string) => BASE_PATH + `/admin/doi/requests/${requestNo}/retract`,
    ADMIN_RETRACT_REJECT: (requestNo: number | string) => BASE_PATH + `/admin/doi/requests/${requestNo}/retract-reject`,
    ADMIN_RETRACT_BY_DATA: (dataNo: number | string) => BASE_PATH + `/admin/doi/data/${dataNo}/retract`,
    RETRACTED_DATA_BY_PROJECT: (projectId: number | string) => BASE_PATH + `/projects/${projectId}/retracted-data`,
  },
}

export const EXCEPT_CHECK_TOKEN_LIST = [
  API_URL.AUTH.LOGIN,
  API_URL.AUTH.CHECK_ID,
  API_URL.AUTH.REQUEST_ACCOUNT,
  API_URL.CHECK_HAS_TABLES,
  API_URL.INIT_DB_TABLES,
  API_URL.TEST.PING,
  API_URL.OAUTH.AUTHORIZE,
  API_URL.OAUTH.AUTH_CODE,
  API_URL.OAUTH.TOKEN,
  API_URL.OAUTH.CLIENT,
  API_URL.HOME.DATA,
  API_URL.HOME.SEARCH_DATA,
  API_URL.HOME.SEARCH_ITEMS,
]
